import type { MouseEvent, ReactNode } from 'react'

export interface ModalProps {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}

/**
 * 460px wide, radius 14px, header/body/footer. Body scrolls; the panel caps
 * to viewport height. The overlay uses align-items:flex-start (per
 * docs/design-handoff/README.md "Responsive behaviour") so a tall modal
 * stays reachable on a short viewport instead of being centred off-screen.
 */
export function Modal({ open, title, onClose, children, footer }: ModalProps) {
  if (!open) return null

  const stop = (e: MouseEvent) => e.stopPropagation()

  return (
    <div
      // p-4 on a phone, p-6 above it: 24px of gutter each side costs 48px of
      // a 390pt screen, which the dialog needs more than the backdrop does.
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 md:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={stop}
        // `w-full max-w-[460px]`, not `w-[460px]`: a fixed 460 is WIDER THAN
        // THE PHONE (390pt), so every modal — logging time, closing a thread,
        // changing a ticket — ran off the side of the screen and took the
        // page's horizontal scroll with it. 460 was only ever a maximum.
        className="mt-10 flex max-h-full w-full max-w-[460px] flex-col overflow-hidden rounded-[14px] border border-subtle bg-elevated shadow-modal"
      >
        <div className="border-b border-subtle px-4 py-3 text-[14px] font-semibold text-hi">{title}</div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[13px] text-mid">{children}</div>
        {footer ? <div className="border-t border-subtle px-4 py-3">{footer}</div> : null}
      </div>
    </div>
  )
}
